import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, message } from 'antd';
import {
  LockOutlined,
  ArrowLeftOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { images } from '../assets';
import Logo from '../Component/Logo';
import LanguageSwitcher from '../Component/LanguageSwitcher';
import Api from '../Network';
import { ResetPasswordProps } from '../types/types';

const { Title, Text } = Typography;

const ResetPassword: React.FC<ResetPasswordProps> = ({ phoneNumber, role, onBack, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const onFinish = async (values: { password: string; password_confirmation: string }) => {
    setLoading(true);

    try {
      const response: any = await Api.post('/api/password/reset', {
        phone: phoneNumber,
        role: role || 'admin',
        password: values.password,
        password_confirmation: values.password_confirmation,
      });

      console.log('Reset Password Response:', response);

      if (response && (response.success === true || response.status === true)) {
        message.success(response?.message || t('auth.passwordResetSuccess'));
        if (onSuccess) {
          onSuccess();
        }
      } else {
        const errorMsg = response?.message || response?.msg || t('auth.passwordResetFailed');
        message.error(errorMsg);
      }
    } catch (err: any) {
      console.error('Reset Password Error:', err);
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.msg ||
        err?.message ||
        t('auth.passwordResetFailed');
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.startsWith('966')) {
      const number = cleaned.substring(3);
      return `+966 ${number.substring(0, 2)} ${number.substring(2, 5)} ${number.substring(5)}`;
    }
    return phone;
  };

  return (
    <div className="responsive-login">
      <div
        className="login-bg-pattern"
        style={{ backgroundImage: `url(${images.bg})` }}
        aria-hidden
      />

      <div className="login-branding">
        <div className="login-branding-inner">
          <Logo size="medium" invert={true} style={{ marginBottom: '30px' }} />
          <Title level={1} className="brand-title">
            {t('branding.DES')}
          </Title>
          <Title level={3} className="brand-subtitle">
            {t('branding.retailManagement')}
          </Title>
          <Text className="brand-tagline">{t('branding.streamlineOperations')}</Text>
        </div>
      </div>

      <div className="login-form">
        <Card>
          <div className="login-form-header">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={onBack}
              className="login-form-back-btn"
            />
            <LockOutlined className="login-form-icon" />
            <Title level={2}>{t('auth.resetPasswordTitle')}</Title>
            <Text type="secondary">{t('auth.resetPasswordDescription')}</Text>
            <Text className="login-form-highlight">{formatPhoneNumber(phoneNumber)}</Text>
          </div>

          <Form
            form={form}
            name="resetPassword"
            onFinish={onFinish}
            autoComplete="off"
            layout="vertical"
            size="large"
          >
            <Form.Item
              name="password"
              rules={[
                { required: true, message: t('validation.passwordRequired') },
                { min: 6, message: t('validation.passwordMinLength') },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder={t('auth.newPassword')}
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              />
            </Form.Item>

            <Form.Item
              name="password_confirmation"
              dependencies={['password']}
              rules={[
                { required: true, message: t('validation.confirmPasswordRequired') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t('validation.passwordsDoNotMatch')));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder={t('auth.confirmPassword')}
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: '24px' }}>
              <Button type="primary" htmlType="submit" loading={loading} block>
                {loading ? t('auth.resetting') : t('auth.resetPassword')}
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>

      <div className="login-language-switcher">
        <LanguageSwitcher type="button" />
      </div>
    </div>
  );
};

export default ResetPassword;
